// Trip API service
// This file contains API calls for trip management

import { getTokensFromCache } from '../../auth/utils/tokenCache';

export interface TripDates {
  type: 'concrete' | 'flexible';
  startDate: string; // YYYY-MM-DD format required by backend
  endDate: string; // YYYY-MM-DD format required by backend
  startOffsetDaysPositive?: number;
  startOffsetDaysNegative?: number;
  endOffsetDaysPositive?: number;
  endOffsetDaysNegative?: number;
}

export interface Trip {
  id: string | number;
  title: string;
  destinations: string[];
  dates: TripDates;
  status: string;
  images?: string[];
  activities?: string[];
  budget?: string | number | { amount: number; currency: string };
  description?: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  progress?: number;
  rating?: number;
  memories?: number;
  conversation_id?: string;
}

export interface CreateTripRequest {
  title: string;
  destinations: string[];
  dates: TripDates;
  status: 'draft' | 'planning' | 'in_progress' | 'completed'; // Must match backend TripStatus enum
  images?: string[];
  activities?: string[];
  budget?: {
    amount: number;
    currency: string;
  };
  description?: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  conversation_id?: string;
}

export interface UpdateTripRequest extends Partial<CreateTripRequest> {
  id: string;
}

export interface Activity {
  id: string;
  tripId: string;
  title: string;
  location: string;
  dateTime?: string; // ISO 8601 format
  budget?: {
    amount: number;
    currency: string;
  };
  status: 'scheduled' | 'planned';
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateActivityRequest {
  title: string;
  location: string;
  dateTime?: string; // ISO 8601 format
  budget?: {
    amount: number;
    currency: string;
  };
}

export interface UpdateActivityRequest extends Partial<CreateActivityRequest> {
  id: string;
}

const API_ENDPOINT = process.env.REACT_APP_APIGATEWAY_TRIPS_ENDPOINT;

// Get access token from cache using proper token cache utility
const getAccessToken = (): string | null => {
  try {
    const tokens = getTokensFromCache();
    return tokens?.accessToken || null;
  } catch {
    return null;
  }
};

export const getTrips = async (): Promise<Trip[]> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const url = `${API_ENDPOINT}/trips`;
  console.log('🌐 getTrips API call:', { url });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // No Bearer prefix - API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ getTrips failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to fetch trips: ${response.statusText}`);
  }

  const data = await response.json();
  console.log('✅ getTrips response:', data);
  console.log('✅ Trips array:', data.trips);
  console.log('✅ Trip IDs from backend:', (data.trips || []).map((t: Trip) => ({ id: t.id, title: t.title })));
  return data.trips || [];
};

export const getTripById = async (tripId: string | number): Promise<Trip> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}`;
  console.log('🌐 getTripById API call:', { url, tripId: tripId });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ getTripById failed:', { status: response.status, statusText: response.statusText, errorText, tripId });
    throw new Error(`Failed to fetch trip: ${response.statusText}`);
  }

  const data = await response.json();
  console.log('✅ getTripById response:', data);
  return data.trip;
};

export const createTrip = async (tripData: CreateTripRequest): Promise<Trip> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const response = await fetch(`${API_ENDPOINT}/trip`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(tripData),
  });

  if (!response.ok) {
    throw new Error(`Failed to create trip: ${response.statusText}`);
  }

  const data = await response.json();
  return data.trip;
};

export const updateTrip = async (tripData: UpdateTripRequest): Promise<Trip> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripData.id);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}`;
  console.log('🌐 updateTrip API call:', { url, tripId: tripData.id });

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(tripData),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ updateTrip failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to update trip: ${response.statusText}`);
  }

  const data = await response.json();
  return data.trip;
};

export const deleteTrip = async (tripId: string | number): Promise<void> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}`;
  console.log('🌐 deleteTrip API call:', { url, tripId });

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ deleteTrip failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to delete trip: ${response.statusText}`);
  }

  console.log('✅ Trip deleted successfully');
};

// Activity API functions

export const getActivities = async (tripId: string): Promise<Activity[]> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}/activities`;
  console.log('🌐 getActivities API call:', { url, tripId });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ getActivities failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to fetch activities: ${response.statusText}`);
  }

  const data = await response.json();
  return data.activities || [];
};

export const createActivity = async (tripId: string, activityData: CreateActivityRequest): Promise<Activity> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // Determine status based on whether dateTime is provided
  const status = activityData.dateTime ? 'scheduled' : 'planned';

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}/activity`;
  console.log('🌐 createActivity API call:', { url, tripId, activityData, status });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify({
      ...activityData,
      status
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ createActivity failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to create activity: ${response.statusText}`);
  }

  const data = await response.json();
  return data.activity;
};

export const updateActivity = async (tripId: string, activityId: string, activityData: Partial<CreateActivityRequest>): Promise<Activity> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // Determine status based on whether dateTime is provided
  const status = activityData.dateTime ? 'scheduled' : 'planned';

  // URL-encode the tripId and activityId (UUIDs) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const encodedActivityId = encodeURIComponent(activityId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}/activity/${encodedActivityId}`;
  console.log('🌐 updateActivity API call:', { url, tripId, activityId });

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify({
      ...activityData,
      status
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ updateActivity failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to update activity: ${response.statusText}`);
  }

  const data = await response.json();
  return data.activity;
};
